import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { usePrinters, useCreatePrinter, useUpdatePrinter } from '../hooks/usePrinters'
import { useCreateSavingsGoal } from '../hooks/useSavingsGoals'
import { useCreateSchedule } from '../hooks/useMaintenance'
import { BackLink } from '../components/BackLink'

export default function PrinterFormPage() {
  const { id } = useParams<{ id?: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const { data: printers } = usePrinters()
  const createMutation = useCreatePrinter()
  const updateMutation = useUpdatePrinter()
  const createSavingsGoal = useCreateSavingsGoal()
  const createSchedule = useCreateSchedule()
  const existing = printers?.find((p) => p.id === id)

  // Use a state initializer + only-once sync on existing for edit (avoid set-state-in-effect lint rule)
  const initial = existing
    ? {
        name: existing.name,
        purchaseCost: String(existing.purchase_cost),
        purchaseDate: existing.purchase_date ?? '',
        avgWattage: String(existing.avg_wattage),
        estimatedLifeYears: String(existing.estimated_life_years),
        estimatedUptimePct: String(existing.estimated_uptime_pct),
        annualMaintenanceCost: String(existing.annual_maintenance_cost),
        bufferFactor: String(existing.buffer_factor),
      }
    : {
        name: '',
        purchaseCost: '',
        purchaseDate: new Date().toISOString().slice(0, 10),  // default to today — fix recoup goal scoping
        avgWattage: '150',
        estimatedLifeYears: '3',
        estimatedUptimePct: '0.50',
        annualMaintenanceCost: '75',
        bufferFactor: '1.30',
      }

  const [draft, setDraft] = useState(initial)
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')
    const payload = {
      name: draft.name.trim(),
      purchase_cost: Number(draft.purchaseCost),
      purchase_date: draft.purchaseDate || null,
      status: 'active' as const,
      avg_wattage: Number(draft.avgWattage),
      estimated_life_years: Number(draft.estimatedLifeYears),
      estimated_uptime_pct: Number(draft.estimatedUptimePct),
      annual_maintenance_cost: Number(draft.annualMaintenanceCost),
      buffer_factor: Number(draft.bufferFactor),
      additional_upfront_cost: 0,
      notes: null,
    }
    try {
      if (isEdit && id) {
        await updateMutation.mutateAsync({ id, ...payload })
      } else {
        const created = await createMutation.mutateAsync(payload)
        // Auto-create recoup goal for new printers
        await createSavingsGoal.mutateAsync({
          name: `Recoup ${created.name}`,
          target_amount: created.purchase_cost,
          start_date: created.purchase_date ?? new Date().toISOString().slice(0, 10),
          linked_printer_id: created.id,
          notes: null,
          completed_at: null,
          display_color: null,
        })
        // Auto-create default maintenance schedules for new printers
        const defaultSchedules = [
          { task_type: 'nozzle-swap', interval_type: 'hours' as const, interval_value: 200 },
          { task_type: 'belt-tension', interval_type: 'hours' as const, interval_value: 80 },
          { task_type: 'lube-rails', interval_type: 'hours' as const, interval_value: 100 },
          { task_type: 'hotend-clean', interval_type: 'hours' as const, interval_value: 150 },
          { task_type: 'bed-level', interval_type: 'hours' as const, interval_value: 50 },
        ]
        for (const sched of defaultSchedules) {
          await createSchedule.mutateAsync({
            printer_id: created.id,
            ...sched,
          })
        }
      }
      navigate('/settings/printers')
    } catch (err) {
      setErrorMessage((err as Error).message)
    }
  }

  const set = (key: keyof typeof draft) => (v: string) => setDraft({ ...draft, [key]: v })

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 560 }}>
      <BackLink fallback="/settings/printers" />
      <h1>{isEdit ? 'Edit printer' : 'Add printer'}</h1>
      <form onSubmit={handleSubmit} style={{ marginTop: 'var(--space-6)' }}>
        <Input label="Name" value={draft.name} onChange={set('name')} required hint="e.g., 'P2S'" />
        <Input label="Purchase cost ($)" value={draft.purchaseCost} onChange={set('purchaseCost')} required type="decimal" />
        <Input label="Purchase date" value={draft.purchaseDate} onChange={set('purchaseDate')} type="date" />

        <h3 style={{ marginTop: 'var(--space-6)', marginBottom: 'var(--space-3)', fontSize: 'var(--font-size-base)' }}>
          PFA pricing inputs
        </h3>
        <Input label="Average wattage (W)" value={draft.avgWattage} onChange={set('avgWattage')} type="decimal" hint="FDM printers usually 60–200W. Bambu P1S/P2S ~80W." />
        <Input label="Estimated life (years)" value={draft.estimatedLifeYears} onChange={set('estimatedLifeYears')} type="decimal" hint="3 — PFA default for hobbyist printers" />
        <Input label="Estimated uptime %" value={draft.estimatedUptimePct} onChange={set('estimatedUptimePct')} type="decimal" hint="0.50 = 50% — PFA default" />
        <Input label="Annual maintenance ($)" value={draft.annualMaintenanceCost} onChange={set('annualMaintenanceCost')} type="decimal" hint="$75 — PFA default" />
        <Input label="Buffer factor" value={draft.bufferFactor} onChange={set('bufferFactor')} type="decimal" hint="1.30 = 30% buffer for unforeseen — PFA default" />

        <button
          type="submit"
          disabled={createMutation.isPending || updateMutation.isPending}
          style={{
            marginTop: 'var(--space-6)',
            padding: 'var(--space-3) var(--space-5)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
          }}
        >
          {createMutation.isPending || updateMutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add printer'}
        </button>

        {errorMessage && (
          <p style={{ marginTop: 'var(--space-3)', color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
            {errorMessage}
          </p>
        )}
      </form>
    </div>
  )
}

function Input({ label, value, onChange, type = 'text', hint, required = false }: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: 'text' | 'date' | 'decimal'
  hint?: string
  required?: boolean
}) {
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label style={{
        display: 'block',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
        marginBottom: 'var(--space-1)',
      }}>
        {label} {required && <span style={{ color: 'var(--color-danger)' }}>*</span>}
      </label>
      <input
        type={type === 'date' ? 'date' : 'text'}
        inputMode={type === 'decimal' ? 'decimal' : undefined}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: 'var(--space-3)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
        }}
      />
      {hint && (
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginTop: 'var(--space-1)' }}>
          {hint}
        </p>
      )}
    </div>
  )
}
